/**
 * Timeline — three-tier performance primitive (Action / Song / Genre).
 *
 * Mental model:
 *   - A Timeline is sections + lanes + steps. Same primitive at all three tiers.
 *   - Tier discriminator (`tier`) picks scope + runtime semantics; schema shape
 *     is identical.
 *   - Action Timelines: short, bound to a button, fired on press. Typically one
 *     section.
 *   - Song Timelines: bar-counted, section-jumpable, started explicitly.
 *   - Genre Timelines: continuous night-wide underlay, one active at a time,
 *     loop by default.
 *
 * Runtime contract (v1 target — Milestone 1 is schema + PWA library only):
 *   OPBridge runs the timeline (`opbridge-timelines` crate, cross-project ask
 *   filed in OPApps/opbridge-opbuttons.md). PWA authors, previews, subscribes to
 *   `/engines/{consumer}/state` for live playhead. Same model as
 *   Sequencer/Macro/LFO per DEC-006 ("bridge ticks, PWA paints") and DEC-024.
 *
 * See TIMELINE-DESIGN.md for the full spec + DEC-023 through DEC-033 for locks.
 *
 * Schema-reserved (v1 UI won't expose; store now so authored data is
 * forward-compatible per feedback_basic_advanced_controls.md):
 *   - Section.onEnter        — Action Timeline id to fire on section enter
 *   - Section.onEnd          — 'auto-advance' | 'stop' | 'loop' behaviour
 *   - Section.color          — persistent per-name across timelines
 *   - Lane.mute / Lane.solo  — runtime controls
 *   - Lane.autoFire          — false = manual arm required when playhead
 *                              crosses lane content
 *   - Step.snap              — per-step override of timeline default
 *   - AnimationStep.params   — preset-specific parameter bag
 *
 *   Runtime ignores fields it doesn't understand.
 */

import type { MotionShape } from './actions.js';

// =====================================================================
// TIER
// =====================================================================

/** Three-tier scope discriminator. Same primitive; runtime semantics differ. */
export type TimelineTier = 'action' | 'song' | 'genre';

// =====================================================================
// STEP
// =====================================================================

/** Placement snap when authoring (bar / beat / 16th / 32nd / free). Default
 *  is bar (§10 Q1 answer 2026-07-01). Reserved on individual steps for
 *  per-step override; timeline-level `defaultSnap` is where the current
 *  authoring snap lives. */
export type TimelineSnap = 'bar' | 'beat' | '16th' | '32nd' | 'off';

/** Step kind discriminator. Kinds that ship in M1 schema (UI arrives at later
 *  milestones):
 *    - `cue`         — single-instant fire (any action)
 *    - `animation`   — duration-spanning parametric FX (hold / fade / strobe /
 *                      chase / pulse / colorSweep / random). The "FX" content
 *                      on a `dmx-rig`/`dmx-fixture` lane (2026-08-04).
 *    - `sequencer`   — reference to a Sequencer by id
 *    - `macro`       — reference to a Macro by id
 *    - `buttonRef`   — reference to a layout button by id (M1.5). Drop-from-
 *                      palette source. Firing resolves the button's current
 *                      action at fire time (renames + action edits flow
 *                      through automatically).
 *    - `colorMarker` — colour keyframe, directly on a `dmx-rig`/`dmx-fixture`
 *                      lane (2026-08-04 — previously a dedicated fixture-color
 *                      sub-lane; the sub-lane concept retired, the marker +
 *                      its interpolation did not). The "Colour" content on
 *                      those lane kinds.
 *    - `marker`      — text-only annotation, no fire
 *
 *  RETIRED 2026-08-04: `valuePoint` (per-capability automation keyframe on a
 *  `fixture-cap` sub-lane) — the whole fixture-group/fixture-cap/fixture-color
 *  sub-lane system retired in favor of flat `dmx-rig`/`dmx-fixture` lanes
 *  holding `animation` (FX) + `colorMarker` (colour) directly. Old persisted
 *  data with `valuePoint` steps or the old lane kinds is tolerated on load
 *  (never crashes) but not rendered — see `timeline/store.ts`'s loader.
 */
export type TimelineStepKind =
  | 'cue'
  | 'animation'
  | 'sequencer'
  | 'macro'
  | 'buttonRef'
  | 'colorMarker'
  | 'lightRelease'
  | 'marker'
  | 'targetValue'
  | 'automationPoint'
  | 'pattern'
  | 'hitRoute'
  | 'hitBlock'
  | 'hit';

/** Segment interpolation from one value point to the next.
 *  `linear` = straight-line between endpoints.
 *  `hold`   = flat at the outgoing value until the next point.
 *  `ease-in`      = accelerates out of the outgoing value.
 *  `ease-out`     = decelerates into the incoming value.
 *  `ease-in-out`  = both. */
export type ValuePointCurve = 'linear' | 'hold' | 'ease-in' | 'ease-out' | 'ease-in-out';

/** Position within the timeline, expressed as fractional bars from timeline
 *  start (bar 0 = the very beginning). Fractional so a step at "bar 3 beat 2
 *  of 4/4" is `2 + 1/4 = 2.25`. Runtime does the quantize-to-snap math when
 *  the user drags a step; storage keeps the raw fraction. */
export type BarPosition = number;

interface StepBase {
  /** ULID, stable across edits (§10 Q2 answer 2026-07-01). */
  id: string;
  /** Fractional bars from timeline start. */
  position: BarPosition;
  /** RESERVED — per-step snap override. Undefined = follows timeline default. */
  snap?: TimelineSnap;
}

export interface CueStep extends StepBase {
  kind: 'cue';
  /** Free-form action payload. v1 shape mirrors the layout action steps
   *  (see actions.ts / layout ActionStep) but stored as `unknown` here so
   *  the schema package stays free of PWA-layer dependencies. */
  action?: unknown;
  /** What the editor calls it ("Column 3", "Layer 2, column 3: Neon"), 2026-10-13 (the OPVisual lane). Ours only:
   *  in the known-keys map, so never sent. */
  label?: string;
}

export interface AnimationStep extends StepBase {
  kind: 'animation';
  /** Duration in bars (fractional allowed). */
  durationBars: number;
  /** A preset id from the home bridge's `GET /lighting/presets` (OPBridge's 23: flow, beat-hit, candle, ...; their
   *  S295). The editor offers only that list; a block naming one the bridge lacks comes back as a push warning.
   *  (Until 2026-10-19 this listed hold / fade / strobe / chase / pulse / colorSweep / random, which OPBridge never had.) */
  preset: string;
  /** The preset's knobs by key (OPBridge refuses an unknown key by name). The editor sets none yet: defaults play. */
  params?: Record<string, unknown>;
}

export interface SequencerRefStep extends StepBase {
  kind: 'sequencer';
  /** Reference into the SequencerLibrary. Editing the referenced sequencer
   *  in its own editor affects every timeline placement (DEC-026). */
  sequencerId: string;
}

export interface MacroRefStep extends StepBase {
  kind: 'macro';
  /** Reference into the MacroLibrary. See sequencer note above. */
  macroId: string;
}

/** Reference to a fireable layout item - a Button, or any other item kind
 *  whose action is a single discrete trigger with no value/duration
 *  complexity (PWA-side allowlist: ButtonItem, PresetChangeItem,
 *  SnapshotChangeItem, StreamDeckItem - see `PaletteFireableItem` in
 *  TimelineEditor.tsx; the kind name stayed `buttonRef` on widening
 *  2026-08-15 rather than renaming an already-shipped step kind).
 *  Firing resolves the referenced item's current action at fire time from
 *  the active project, so renames + inspector edits flow through
 *  automatically. The buttonId is scoped by the project the timeline was
 *  authored under; if the referenced item no longer exists at fire time
 *  runtime skips the step and surfaces a warning. */
export interface ButtonRefStep extends StepBase {
  kind: 'buttonRef';
  /** Id of the referenced layout item (any `PaletteFireableItem`, not only
   *  ButtonItem despite the field name - see this type's doc comment). */
  buttonId: string;
  /** Optional cached display label so the editor can render the placement
   *  block even if the button has been deleted. Purely informational — fire
   *  resolves through the live button lookup. */
  labelHint?: string;
  /** Optional duration in bars. When absent, the runtime fires the button
   *  as instant (single trigger). When present, the runtime holds the
   *  button's ON state for `durationBars` and then fires OFF — behaves
   *  like a scheduled press-hold-release. Editor-authored via the block's
   *  edge resize; defaults to instant on drop. */
  durationBars?: number;
}

/** Set one live-state target to a value at this bar position - the
 *  Milestone 1 slice of the Rig State primitive (2026-08-15, PLAN.md QUEUED
 *  "Rig States" > "Resequenced 2026-08-15"), scoped to the three targets
 *  that already have a reactive live-value source and no dedicated
 *  TimelineStep of their own: Transpose, Capo, Waves Autotune key/scale.
 *  Authored two ways, both producing this same shape: dragged from the
 *  Timeline editor's live-state drawer (`value` = whatever was live at drag
 *  time - a capture), or hand-edited afterward via the placed step's own
 *  editor (an explicit pick - safe here because every one of these targets
 *  has a narrow, always-valid, always-knowable domain, unlike an arbitrary
 *  open-loop Rig State bank target). See `liveState/registry.ts` for the
 *  registered target list. */
export interface TargetValueStep extends StepBase {
  kind: 'targetValue';
  /** Opaque id into the live-state target registry (e.g. `'transpose'`,
   *  `'capo'`, `'autotuneKey'`, `'autotuneScale'`). Deliberately a plain
   *  string, not a closed union - matches the CP-058 targetId philosophy of
   *  staying extensible without a schema migration when a new target is
   *  registered. */
  targetId: string;
  /** The raw value to set - semitones for transpose/capo, a pitch class
   *  0-11 for autotuneKey, an `AutotuneScale` string for autotuneScale.
   *  Interpretation is the registered target's job, not this step's. */
  value: number | string;
  /** Optional cached display label, same pattern as `ButtonRefStep.labelHint`
   *  - lets the editor render the block even if the live-state registry
   *  entry it came from has since changed shape. */
  labelHint?: string;
}

export interface MarkerStep extends StepBase {
  kind: 'marker';
  /** Human-readable annotation for the performer. Not fired. */
  text: string;
}

/** Which color space the segment out of a marker interpolates in.
 *  - `sRGB`  = componentwise linear on stored RGB. Passes through muddy
 *              midtones (red→blue via brown). Legacy default.
 *  - `HSL`   = lerp hue/saturation/lightness. Passes through the hue wheel
 *              (red→blue via magenta on short path, via yellow-green-cyan
 *              on long path). What most stage-lighting software does.
 *  - `OKLCH` = perceptually-uniform lerp. Cleaner rainbows than HSL, no
 *              yellow spike from red→green. */
export type ColorInterpolationSpace = 'sRGB' | 'HSL' | 'OKLCH';

/** For HSL / OKLCH interpolation only: which way around the hue wheel.
 *  `short` = the smaller of the two arcs (default; feels like a natural
 *  fade). `long` = the longer arc; produces rainbow sweeps. */
export type HuePathDirection = 'short' | 'long';

/** Colour keyframe, authored directly on a `dmx-rig`/`dmx-fixture` lane
 *  (2026-08-04 — previously fired on a dedicated fixture-group `color`
 *  sub-lane; the sub-lane/group concept retired, this marker + its
 *  interpolation moved onto the flat lane unchanged). The lane holds a
 *  series of ColorMarkerSteps; consecutive markers define a segment across
 *  which the runtime interpolates R+G+B (+W when present) toward the
 *  incoming color, with the outgoing marker's `curve` selecting the
 *  segment shape.
 *
 *  Storage:  authored RGB in 0..1 per channel + optional white.
 *  Fire:     runtime decomposes into R/G/B (+W) capability writes — for a
 *            `dmx-fixture` lane, to that lane's `fixtureRef.fixtureId`; for
 *            a `dmx-rig` lane, to every fixture in the rig at once. */
export interface ColorMarkerStep extends StepBase {
  kind: 'colorMarker';
  /** Authored color, 0..1 per channel. `w` is optional white; when the
   *  fixture has a color.w capability the runtime pushes it directly,
   *  when absent it's folded into r/g/b at sample time. */
  color: { r: number; g: number; b: number; w?: number;
    /** Warm-to-cool of the white LEDs `w` drives (OPBridge S329, `timeline_white_leds`): 0 all warm, 1 all cool,
     *  absent both at `w`. Fades between keyframes like `w`; a master's mix drops it, as it drops `w`. Only a light with
     *  white LEDs (the PL60C) uses it; others fold `w` into RGB. */
    wTemp?: number };
  /** Optional label — human name of a preset ("Amber", "Deep Blue"). */
  label?: string;
  /** Segment shape from this marker to the next. Defaults 'linear'. */
  curve?: ValuePointCurve;
  /** Interpolation color space out of this marker. Defaults 'sRGB' for
   *  back-compat with pre-2026-07-07 authored data. */
  space?: ColorInterpolationSpace;
  /** Hue-wheel direction for HSL / OKLCH segments. Defaults 'short'.
   *  Ignored when `space` is sRGB (no wheel to walk). */
  path?: HuePathDirection;
  /** On an "All lights" (`dmx-rig`) lane, the keyframe that STARTS a
   *  takeover (2026-10-02, operator: "the lighting rig should have a master
   *  group track which overrides any individual automation"): the bars
   *  before it over which every light fades from what it shows into this
   *  colour, arriving on this beat. Absent or 0 = a cut on the beat. The
   *  maths is `timeline/lightMix.ts`. */
  leadInBars?: number;
  /** The shape of that fade (the fade shapes; defaults 'linear'). */
  leadInCurve?: ValuePointCurve;
  /** A SPLIT keyframe (2026-11-02, operator: "fade to red to black in bar 1 and then at start of bar 2 i want blue ...
   *  fade for in between and split diamond on keyframe for cut"): the colour the stretch before this keyframe ARRIVES
   *  at, the left half of its diamond, while `color` (the right half) cuts in on this beat. Absent = the stretch arrives
   *  at `color`, as before. It plays as two keyframes on one beat, this colour first (`unrollPatterns`), which is how
   *  OPBridge already plays two keys on one beat (its stable sort, `colour_lanes.rs::own_colour`, read 2026-11-02). */
  arrive?: ColorMarkerStep['color'];
}

/** "Hand back" on an "All lights" (`dmx-rig`) lane (2026-10-02): the
 *  takeover started by the last keyframe before it ends here, and every
 *  light goes back to its own lane. `leadInBars` is the fade back, ending on
 *  this beat. A point, like a keyframe; never fired on its own. */
export interface LightReleaseStep extends StepBase {
  kind: 'lightRelease';
  leadInBars?: number;
  leadInCurve?: ValuePointCurve;
}

/** A point on an automation lane's curve (2026-10-16, operator: record mode with "presses and fader moves"): the
 *  value the lane's control (a fader, a knob, one axis of an XY pad) is at on this beat, as the 7-bit MIDI value it
 *  sends (0-127). `curve` is how it travels to the next point: `linear` (the default) or `hold` (stays, then steps).
 *  The PWA compiles a lane of these into CC cues for OPBridge (`bridgeSync.ts`); it never crosses the wire itself. */
export interface AutomationPointStep extends StepBase {
  kind: 'automationPoint';
  value: number;
  /** How it travels to the next point. Absent = `linear`. The four other shapes (2026-10-23, OP-Motion's: S-curve,
   *  riser, fall-away, late cut) bend a ramp; a control's lane samples them, an OP-Motion lane sends them as they are. */
  curve?: 'hold' | MotionShape;
}

/** One colour key inside a pattern's cycle: `at` is bars from the start of the cycle (0 <= at < cycleBars); the rest
 *  is a `ColorMarkerStep`'s colour and the shape of its segment to the next key. */
export interface PatternKey {
  at: number;
  color: ColorMarkerStep['color'];
  label?: string;
  curve?: ValuePointCurve;
  space?: ColorInterpolationSpace;
  path?: HuePathDirection;
}

/** A PATTERN block on a lights lane (2026-10-20, operator: "making the timeline more efficient with the blocks instead
 *  of repeating the same patterns. sort of like compression. i think that would help with the performance of the
 *  browser too"). One cycle of `cycleBars` holds `keys`; from `position` it repeats for `durationBars` (the last cycle
 *  may be cut short). It plays EXACTLY as its keys written out (`unrollPatterns`): the editor draws one block where a
 *  groove was hundreds of keyframes, and OPBridge is sent the written-out keys, so it never sees a pattern.
 *  The block OWNS its span: a plain keyframe inside it is ignored. */
export interface PatternStep extends StepBase {
  kind: 'pattern';
  durationBars: number;
  cycleBars: number;
  keys: PatternKey[];
  label?: string;
}

/** A WAIT in a chain (2026-10-23, OPVisual's ask "a timeline's steps wait in bars, beats OR plain ms"; OPBridge S307
 *  `timeline_chains`): the pause before the next step. `ms` is wall-clock time and runs with the transport stopped;
 *  `beats` / `bars` count on the musical clock and stretch with tempo; `nextBar` (no `amount`) snaps to the next bar
 *  line. Only meaningful on a chain (`Timeline.form: 'chain'`), whose steps run in ARRAY order: its `position` is
 *  just the step's index there. */
export type WaitUnit = 'ms' | 'beats' | 'bars' | 'nextBar';
export interface WaitStep extends StepBase {
  kind: 'wait';
  unit: WaitUnit;
  /** How many of `unit`; absent for `nextBar`. OPBridge caps a wait at an hour or 1024 bars. */
  amount?: number;
}

// ---- drum lights: the `instrument` lane (2026-10-23; wire agreed with OPBridge S331, OPApps/opbridge-opbuttons.md) ----
//
// A hit played live (a kick, a snare: OP-MidiFeed on a Live track, or a note straight off a MIDI port) lights the rig
// through the timeline that is running. A `hitRoute` step says "from this bar, hits of this source light this look"
// and holds until the next route on its lane for the same source. Looks are named, on the timeline
// (`instrumentLooks`). How a hit draws is `pwa/src/timeline/hitMix.ts`, which OPBridge ports. Every value below is
// one OPBridge's parser accepts (`opbridge-core/src/live_lights.rs`, read 2026-10-23): one look it cannot read makes it
// drop EVERY look of the timeline, so the compile sends nothing else.

/** The drums OP-MidiFeed's drum mode names (OPBridge `live_lights::ROLES`). `tom1-3` and `crash1-2` came with the
 *  device's v0.1-rc4 (2026-10-30, the operator's full kit), and the picker opens on the kit; the older names stay after it, the device and saved blocks still
 *  use them. */
export const HIT_ROLES = ['kick', 'snare', 'hat', 'openhat', 'tom1', 'tom2', 'tom3', 'crash1', 'crash2', 'cymbal', 'tom', 'clap', 'perc'] as const;
export type HitRole = (typeof HIT_ROLES)[number];

/** What a route listens for; every field given must match, and one with none matches nothing. `role` = a drum
 *  OP-MidiFeed classified; `track` = a Live track (OP-MidiFeed's name for it, spaces as `_`); `port` = notes played
 *  straight into that MIDI input (the kit with Live closed), all of them or only `note`. A port source hears only the
 *  port, the others only OP-MidiFeed, so one source never hears a pad twice. */
export interface HitSource { role?: string; track?: string; note?: number; port?: string }

/** How one hit draws: `flash` = full on, falling into what is lit beneath; `pulse` = swells up and back down;
 *  `strobe` = strobes for its length, and plays only in a show that opts in (`Timeline.strobes`). */
export type InstrumentLookStyle = 'flash' | 'pulse' | 'strobe';
/** How a look moves on from hit to hit: `none` = every light every hit; `next` = the next light each hit (a chase);
 *  `random` = one light at random; `alternate` = the first half, then the second half. */
export type InstrumentLookVary = 'none' | 'next' | 'random' | 'alternate';
/** The fall's shape (OPBridge's `Curve`): `easeOut` (the default: fast drop, long tail), `linear`, `smooth`, `easeIn`. */
export type InstrumentLookCurve = 'easeOut' | 'linear' | 'smooth' | 'easeIn';
export type InstrumentColor = ColorMarkerStep['color'];

/** A named look on a timeline, exactly as OPBridge reads it (`live_lights::TimelineLook`). `lights` are fixture ids on
 *  the PC the timeline plays on (DMX, `elgato:`, `wled:`; a `hue:` ceiling light is dropped there); `group` adds every
 *  lights lane of that `lightGroup`. `palette` (2+ colours) = each hit takes the next colour, else `color` (white when
 *  absent). Beats follow the live tempo. `name` is ours (OPBridge ignores it). */
export interface InstrumentLook {
  id: string;
  name?: string;
  lights?: string[];
  group?: string;
  style?: InstrumentLookStyle;
  color?: InstrumentColor;
  palette?: InstrumentColor[];
  /** How long a hit takes to come up; 0 (the default) = at once. */
  attackBeats?: number;
  /** How long a hit takes to fall back; OPBridge's default is half a beat. */
  fallBeats?: number;
  curve?: InstrumentLookCurve;
  /** Brightness 0..1 (the default 1). */
  level?: number;
  vary?: InstrumentLookVary;
  /** How much a softer hit dims (OPBridge S336, operator "yes and on by default"): a hit of velocity v draws at
   *  `level x (1 - s + s x v / 127)`; 0 = every hit at the full level, absent = 1 (follows velocity all the way). */
  velocitySensitivity?: number;
}

/** On an `instrument` lane: from `position` until the next route on the lane for the same `source`, a hit of `source`
 *  lights the timeline's look `lookId`. A `lookId` that names no look lights nothing (`HIT_SILENCE_LOOK`). */
export interface HitRouteStep extends StepBase {
  kind: 'hitRoute';
  source: HitSource;
  lookId: string;
}

/** A look as a drum lights block holds it: an `InstrumentLook` without its id, plus `allLights` (every light the
 *  timeline's PC has but the ceiling, read when it is sent, so a light added later joins) and `colors` (one = that
 *  colour; several = each hit the next one). */
export interface HitBlockLook {
  lights: string[];
  allLights?: boolean;
  colors: InstrumentColor[];
  style: InstrumentLookStyle;
  vary: InstrumentLookVary;
  fallBeats: number;
  attackBeats?: number;
  curve?: InstrumentLookCurve;
  level?: number;
  /** As `InstrumentLook.velocitySensitivity`: absent = 1 (softer hits dim); a fill that must always land full sets 0. */
  velocitySensitivity?: number;
}

/** A DRUM LIGHTS block on an `instrument` lane (2026-10-23, operator: "for the first 7 bars of the sequence the snare is
 *  using lights X and Y with colours and then for the last 1 bar of the sequence all of the lights would trigger from
 *  the snare, bright white. like a fill" and "i need it to be super simple to set up"; chosen shape "drop a preset
 *  block"; "repeats every 8 bars but might need a 16 or 32 bar loop"). Every `cycleBars` from `position` for
 *  `durationBars` (the last cycle may be cut short), hits of `source` light `look`, and in the last `fill.bars` of each
 *  cycle `fill.look` instead. OPButtons only: it is sent written out (`unrollHitBlock`) as `hitRoute` steps and two
 *  looks, so OPBridge never sees a block, and after its span the source lights nothing until the next route. */
export interface HitBlockStep extends StepBase {
  kind: 'hitBlock';
  durationBars: number;
  cycleBars: number;
  source: HitSource;
  look: HitBlockLook;
  /** The fill at the end of every cycle; absent or `off` = none (kept, so turning it back on finds it as it was). */
  fill?: { bars: number; look: HitBlockLook; off?: boolean };
  label?: string;
}

/** A DESIGNED drum hit on an `instrument` lane (2026-10-27, OPButtons DEC-324; OPBridge S339, `timeline_designed_hits`,
 *  released v1.15.222; operator 2026-10-24: "timelines would have the kick and snare already in there and designed, but
 *  when op-midifeed is connected, the live input overrides the hit triggers"). Played as a live hit of `source` would be
 *  at `position` (the route in force, its look, the cap, velocity), and SILENT while OP-MidiFeed is present and sends
 *  that source (switching on a bar line). `source` is a `role` or a `track` (OPBridge refuses neither). `velocity`
 *  1-127, absent = 127. */
export interface HitStep extends StepBase {
  kind: 'hit';
  source: HitSource;
  velocity?: number;
}

export type TimelineStep =
  | HitRouteStep
  | HitBlockStep
  | HitStep
  | WaitStep
  | PatternStep
  | AutomationPointStep
  | CueStep
  | AnimationStep
  | SequencerRefStep
  | MacroRefStep
  | ButtonRefStep
  | ColorMarkerStep
  | LightReleaseStep
  | MarkerStep
  | TargetValueStep;

// =====================================================================
// LANE
// =====================================================================

/** Lane kind — informational; used by the editor's drop-auto-route to pick
 *  the first matching lane when a palette item is dropped. NOT a runtime
 *  constraint: a lane can hold any step kind (per DEC-028 lanes are user-
 *  named containers, not pre-typed by transport). 'mixed' opts a lane out
 *  of auto-route (matches anything) and is the default when absent.
 *
 *  DMX lanes (redesigned 2026-08-04, replacing the earlier fixture-group /
 *  fixture-cap / fixture-color sub-lane system — DEC pending):
 *   - `dmx-rig`     = controls the WHOLE rig at once. FX (`animation` steps)
 *     + Colour (`colorMarker` steps), no `fixtureRef`.
 *   - `dmx-fixture` = controls ONE fixture. Same FX + Colour content, plus
 *     `fixtureRef.fixtureId` binding the lane to a single `FixtureInstance`.
 *   Both are FLAT — no sub-lanes, no group/capability split. Presets (named,
 *   savable FX/colour combos) are a future layer on top, not built yet.
 *
 *  `guitar` (2026-10-02, OPButtons DEC-244) = the guitar's preset and
 *  snapshot changes: `buttonRef` steps to `presetChange` / `snapshotChange`
 *  canvas items, drawn as STATES (a flag on the change, the preset's name
 *  running to the next one) rather than blocks. Plays exactly like a Controls
 *  lane: the kind is for the editor, and OPBridge keeps it as an extra. */
export type TimelineLaneKind =
  | 'midi'
  | 'osc'
  | 'marker'
  | 'mixed'
  | 'dmx-rig'
  | 'dmx-fixture'
  | 'guitar'
  /** OPVisual cells and columns (2026-10-13): cue steps that fire OPVisual's named actions, which OPBridge's mesh
   *  carries to the PC OPVisual runs on. OPBridge keeps a lane's kind as free text, so it passes through. */
  | 'opvisual'
  /** OPVisual's SHOW COLOUR (2026-10-28, OPVisual's ask, its DEC-174): `colorMarker` keyframes (+ `lightRelease` =
   *  "each clip's own look") that recolour every OPVisual clip which opted in. Never a light: compiled only to
   *  `{module: 'opvisual', command: 'colour' | 'colour_release'}` cues, into every connected computer's copy, so each
   *  OPVisual gets them from its own OPBridge (2026-10-28, "send to every OPVisual"). */
  | 'opvisual-colour'
  /** OPVisual's SHOW PALETTE (2026-10-28, OPVisual's ask): `cue` steps sending `{module: 'opvisual', command:
   *  'palette', args: {id}}`, into every connected computer's copy. Kept apart from the colour (operator: "i definitely
   *  want the 2 separate"). */
  | 'opvisual-palette'
  /** A control's moves over time (2026-10-16): `automationPoint` steps, bound to one canvas control by `control`. */
  | 'automation'
  /** Drum lights (2026-10-23): live hits light the rig through `hitRoute` steps and drum lights blocks (`hitBlock`).
   *  Sent only to a bridge with `timeline_instrument_routing`. */
  | 'instrument'
  /** Synesthesia, the VJ app (2026-11-05, OPBridge S344/S352): `cue` steps sending `{module: 'synesthesia', command,
   *  args}` (a scene, a move, a look), sent to the PC that drives Synesthesia. A scene lasts until the next change, so
   *  the editor draws scene changes as states. OPBridge keeps a lane's kind as free text, so it passes through; a plain
   *  `mixed` lane holding only Synesthesia cues (the lights kit's, before this kind) is treated the same. */
  | 'synesthesia';

/** User-named container for steps. NOT pre-typed by transport (DEC-028) —
 *  the step inside carries its own action type. */
export interface TimelineLane {
  /** ULID. */
  id: string;
  /** Freeform user label (e.g. "Front Wash", "Kick MIDI", "Markers"). */
  name: string;
  /** Informational lane kind used by the editor's auto-route drop target.
   *  Runtime ignores. */
  laneKind?: TimelineLaneKind;
  /** Editor display color. Uses layout's PageHue palette for calm
   *  distinction from control colors. Optional — editor auto-cycles when
   *  missing. */
  color?: string;
  /** Steps in insertion order. UI can sort by position when rendering. */
  steps: TimelineStep[];
  /** Fixture binding for a `dmx-fixture` lane — which `FixtureInstance` this
   *  lane's FX/Colour steps target. Absent on every other lane kind,
   *  including `dmx-rig` (whole rig, no single fixture to bind). RETIRED
   *  2026-08-04: the `capability` sub-field (fixture-cap sub-lanes are
   *  gone — a flat lane has no per-capability split). */
  fixtureRef?: {
    fixtureId: string;
    /** One ZONE of the light (OPBridge S329, `timeline_light_zones`; 0-based, the ColorBand PiX has 0-3). A zone lane
     *  drives that zone; a lane with no `subfixture` fans to every zone that has no lane of its own, whatever the lane
     *  order (`zoneLaneFor`). Absent on every lane made before 2026-10-20. */
    subfixture?: number;
  };
  /** The operator's light group this lights lane sits in (2026-10-03,
   *  OPButtons DEC-251): a name, absent for Lighting itself. On a master
   *  (`dmx-rig`) it scopes the master: a master takes over only the lights in
   *  its OWN group (`lightMix.ts::masterReaches`). Absent on every other kind. */
  lightGroup?: string;
  /** On a master (`dmx-rig`), DEC-329 (OPBridge S318, `timeline_all_lights_master`): it takes over every light whatever
   *  its group, above every group master, and the house lights of the computer playing it. Absent = a group master. */
  allLights?: true;
  /** The canvas control an `automation` lane moves (2026-10-16): a fader or knob by id, or an XY pad and the axis.
   *  Absent on every other kind. */
  control?: { itemId: string; axis?: 'x' | 'y' };
  /** An `automation` lane that turns one of OPVisual's VISUALISER KNOBS instead of a canvas control (2026-10-23,
   *  OPVisual's ask "automation lanes for OPVisual's visualiser knobs"): `macro` 1-7, `layer` 1-5 or absent for the
   *  newest visualiser on air (OPVisual's own default). Points keep the 0-127 scale and are sent as `value` 0..1
   *  (`viz_macro`, OPVisual CONTRACT s4b). Absent on every other lane. */
  vizKnob?: { macro: number; layer?: number };
  /** An `automation` lane that drives another OPVisual control (2026-11-02, operator: "show all controls"): `verb` from
   *  OPVisual CONTRACT s4b, with `layer` 1-5 or the `look` `param` where the verb takes one. How the curve becomes cues
   *  (a value, an on/off, a fire per point) is `pwa/src/timeline/opvControls.ts`. Absent on every other lane. */
  opvControl?: { verb: string; layer?: number; param?: string;
    /** The grid CELL an effect control acts on (2026-11-02, OPVisual's ask "Effect on a cell", its DEC-177): lane 1-5,
     *  column 1-10, sent as `cell: "L<lane>-<col>"`. Only on the `fx` controls. */
    cell?: { lane: number; col: number } };
  /** An `automation` lane that drives OP-MOTION instead (2026-10-23, OPM4LBuilder's ask): the device on Live's submixes
   *  and Main. `buses` = `["all"]` or some of `vocals bvs music bass drums master`; `target` = an effect (`tone` -1..1,
   *  `air`), a Move's amount (`darken lift breathe build`) or the crossfader (`xfade`, Main only). Points keep the 0-127
   *  scale (`timeline/opMotion.ts` maps it; a bipolar target's 64 is exactly its centre) and compile to one
   *  `/OP-Motion/seg` per segment, never to samples: the device draws the curve on Live's beat. `glideOut`: how it
   *  glides back to off where the timeline ends (absent = one bar; the crossfader's absent = none). Absent on every
   *  other lane. */
  motion?: { buses: string[]; target: string; glideOut?: 'none' | 'beat' | 'bar' | '2bars' };
  /** The lane does not play (2026-10-02). Honoured by the PWA's compile
   *  (`bridgeSync.ts::laneAudible`): a silent lane reaches OPBridge with no
   *  steps. OPBridge's engine itself ignores the field. */
  mute?: boolean;
  /** While any lane is soloed, only soloed lanes play (same place). */
  solo?: boolean;
  /** RESERVED — false = crossing the playhead does NOT auto-fire the step;
   *  operator must manually arm. Defaults true (auto-fire) when absent. */
  autoFire?: boolean;
}

// =====================================================================
// SECTION
// =====================================================================

/** On-end behaviour for a section. Auto-advance (default) moves the playhead
 *  to the next section; stop halts; loop replays the section. */
export type SectionOnEnd = 'auto-advance' | 'stop' | 'loop';

export interface TimelineSection {
  /** ULID. */
  id: string;
  /** Freeform ALL CAPS name (DEC-027 — matches OPSetlist convention).
   *  Examples: INTRO, VERSE 1, CHORUS, BRIDGE, DROP, OUTRO. */
  name: string;
  /** Length in bars (integer; arbitrary allowed — 7-bar pre-chorus is fine). */
  lengthBars: number;
  /** RESERVED — persistent per-name color across all timelines. When absent,
   *  editor derives from name hash. */
  color?: string;
  /** RESERVED — end behaviour. Defaults 'auto-advance' when absent. */
  onEnd?: SectionOnEnd;
  /** RESERVED — Action Timeline id to fire once on section-enter. */
  onEnter?: string;
  /** How the LIGHTS arrive when a press jumps here (2026-11-04, OPBridge S351 `timeline_section_arrival`; operator:
   *  "give the user the option, to blend from trigger and land on bar or cut at bar"). `blend`: from the press, every
   *  light the colour lanes paint fades from what it shows into this section's first look, there on the bar line the
   *  jump lands on. Absent (or `cut`) = the cut on the line, as before. A section that opens with a slam always cuts
   *  (`bridgeSync.ts::sectionSlam`), whatever this says. */
  arrival?: TimelineArrival;
}

/** A section's (or a start's) arrival: the lights cut on the bar line, or blend into it from the press. */
export type TimelineArrival = 'cut' | 'blend';

// =====================================================================
// TIMELINE
// =====================================================================

/** Per-tier default section length (bars) used when the editor spawns a
 *  fresh timeline. Song/Action from §10 Q4 answer 2026-07-01 (Action = 1 bar =
 *  4 beats in 4/4). Genre defaults small so a fresh Genre Timeline loops
 *  fast enough to hear the underlay. */
export const TIMELINE_DEFAULT_SECTION_BARS: Record<TimelineTier, number> = {
  action: 1,
  song: 8,
  genre: 4,
};

/** Cap on total bars per Song/Action timeline. Genre timelines are not
 *  bounded by this — their length IS their loop period, typically much
 *  shorter (§10 Q3 answer 2026-07-01). */
export const TIMELINE_MAX_BARS_SONG_ACTION = 1024;

/** One clip of a timeline's song (2026-10-13): at timeline beat `atBeat` (0 = the first beat) the song plays from
 *  its beat `fromBeat` (song bar 1's downbeat = 0; negative = into the intro before bar 1) for `beats`. Clips are
 *  kept ordered and never overlap. `id` is the editor's own; only the three numbers go to OPBridge. */
export interface TimelineSongClip { id: string; atBeat: number; fromBeat: number; beats: number }

export interface Timeline {
  /** Stable id. Referenced by layout buttons as `{ module: 'timeline',
   *  args: { timelineId } }`. */
  id: string;
  /** Display label. Renamable. */
  name: string;
  /** Tier discriminator — same primitive, runtime semantics differ (DEC-032). */
  tier: TimelineTier;
  /** An Action timeline that is a CHAIN (2026-10-23, OPVisual's ask): one lane whose steps run in order - named
   *  actions, and `wait` steps between them in ms, beats, bars or "the next bar" - instead of sitting on a ruler.
   *  Sent to OPBridge as its `"tier": "chain"` (S307). Absent = an ordinary timeline on a ruler. Action tier only. */
  form?: 'chain';
  /** A LIGHTING timeline (2026-10-27, DEC-323; OPBridge S340's "one format"): an ordinary timeline whose lanes are
   *  light lanes only (`dmx-rig`, `dmx-fixture`, `instrument`) and no song, listed apart in the Timelines section and
   *  offered by OPBridge as performance mode's default lights. Set EXPLICITLY (New > Lighting timeline, "Copy as
   *  lighting timeline", the lights kit), never derived from the lanes; dropped by "Make it a full timeline", or by the
   *  store the moment a non-light lane or a song arrives (`timeline/lighting.ts::keepLightingHonest`). Absent = a full
   *  timeline. Nothing that plays a timeline treats it differently. */
  purpose?: 'lights';
  /** Sections in order. Non-empty (editor ensures at least one). */
  sections: TimelineSection[];
  /** Lanes running horizontally across every section. EMPTY by default on a
   *  fresh timeline (2026-08-04 — was auto-seeded with v1 default lanes;
   *  operator asked for a blank slate with an explicit "add a lane" prompt
   *  instead). The editor never assumes at least one lane exists. */
  lanes: TimelineLane[];
  /** Default snap for authoring (DEC-025 quantize is separate — this is
   *  the placement grid, not the section-jump quantize). §10 Q1 answer
   *  2026-07-01: bar. */
  defaultSnap: TimelineSnap;
  /** Informational time signature (numerator/denominator). OPBridge's
   *  MusicalClock is authoritative at runtime; this is for editor bar
   *  math + display. */
  signature?: { num: number; den: number };
  /** Informational default tempo. Same caveat as signature. */
  bpm?: number;
  /** Whole-timeline loop toggle (editor: the control next to Play). When on,
   *  the preview playhead loops back to bar 0 at the end instead of stopping,
   *  and it is the value a TimelineLauncher inherits when its own loop is set
   *  to 'inherit'. Undefined = off (see `timelineLoops`).
   *
   *  Tier semantics (runtime, OPBridge's `opbridge-timelines` engine — parked):
   *  an ACTION timeline loops as a whole; SONG / GENRE timelines express loop
   *  at the SECTION level (`Section.onEnd: 'loop'`), selectable when the
   *  launcher triggers a section. This flag is the top-level intent the PWA
   *  authors + previews; the engine resolves the per-tier granularity. */
  loop?: boolean;
  /** When a timeline takes a fader back after a hand moved it (2026-10-16, OPBridge S324 `timeline_touch_override`):
   *  the next bar line after the hand lets go (the default, also when absent), when the curve reaches the hand's value
   *  (`pickup`), or when the song stops. OPBridge keeps it as sent. */
  touchMode?: TimelineTouchMode;
  /** How the lights arrive when the timeline is STARTED (2026-11-04, OPBridge S351): `blend` fades every light from
   *  what it shows at the press into the look on the bar line the start lands on, from any bar. Absent = a cut, except
   *  that a start at the first bar of a section set to blend blends too (OPBridge's rule). */
  arrival?: TimelineArrival;
  /** Bars BEFORE the first section (2026-10-19, operator: "i need to be able to move the left side of the intro
   *  section. the intro section can start a few bars after the start of the timeline if need be"): a LEAD-IN where the
   *  song plays from its start and nothing else does (lights, presses and cues wait; steps left there are not sent).
   *  Absent or 0 = section 1 starts the timeline, as before. Sent to OPBridge as an unnamed first section
   *  (`timelinePlaySections`), which today's engine plays with no change: unmarked, so a song carries on through it.
   *  Not called `leadInBars`: that name is a lights keyframe's fade and DEC-227's launch strip. */
  firstSectionBar?: number;
  /** What the lights do when the timeline starts (2026-10-20, operator: "when timeline starts i would prefer all lights
   *  to be sent an off signal. so each timeline is consistent ... this could be optional"). `off`: every light is dark
   *  from bar 0 until its own lane first colours it, and a light with no lane stays dark for the run. `asIs`: a light is
   *  left as it was until its lane's first keyframe (the behaviour before the option). Absent = the tier's default
   *  (`timelineStartsDark`). Compiled into what OPBridge plays (`bridgeSync.ts`), so OPBridge needs no field. */
  lightsAtStart?: TimelineLightsAtStart;
  /** What the lights the run drove do when the timeline ENDS (2026-10-20, operator: "at the end of the timeline all the
   *  lights seem to go on full?"): `off` = they end dark; `restore` = OPBridge puts each back as it was before the run
   *  (its behaviour before the option). Absent = the tier's default (`timelineEndsDark`). OPBridge S331 plays it
   *  (`timeline_lights_at_end`), where its own absent means `restore`, so the compile always sends it explicitly. */
  lightsAtEnd?: TimelineLightsAtEnd;
  /** The named looks this timeline's `hitRoute` steps light (2026-10-23, the `instrument` lane), sent as OPBridge reads
   *  them. A drum lights block keeps its looks itself; the compile writes them out beside these. */
  instrumentLooks?: InstrumentLook[];
  /** The show opts in to real strobes (DEC-262: flash-safe by default, strobes a per-show choice): a `strobe` look
   *  plays only where this is true. Sent with the instrument lane. */
  strobes?: boolean;
  /** The project this timeline belongs to (plan 3.1). It travels inside that
   *  project's sync document, so a timeline built on the desktop reaches the
   *  iPad. Undefined on timelines made before projects owned them; the app
   *  gives each one an owner on load (`pwa/src/timeline/librarySync.ts`).
   *  Never sent to OPBridge's timeline engine (it is in the known-keys map). */
  projectId?: string;
  /** The test track this timeline is built against (2026-10-13; plan
   *  `.notepads/test-track-timeline.md`). OPBridge holds the audio, plays it
   *  and keeps its beat map and section marks; this is only the reference, so
   *  the editor can draw that song's waveform and grid under the lanes, each
   *  timeline section showing the stretch of the song marked with its name.
   *  `name` is the label last seen, for when OPBridge cannot be reached.
   *  `pc` / `output`: where the song plays when the timeline brings it (OPBridge
   *  S318 `timeline_test_track`): `pc` = a node_id, absent = the PC the timeline
   *  plays on; `output` = a device name, absent or "" = that PC's default.
   *  Sent to OPBridge as `testTrack: {id, pc?, output?}` only to a bridge that
   *  advertises `timeline_test_track` (bridgeSync.ts::toBridgeTimeline).
   *  `clips` (2026-10-13, operator: "trim and split a song by bar / beatgrid", per timeline): when present, what
   *  plays is these clips, not the song's section marks; a gap between clips is silence. Sent as
   *  `testTrack.clips: [{atBeat, fromBeat, beats}]` only to a bridge advertising `timeline_song_clips`.
   *  `skipIntro` (2026-10-13, operator: "play the intro, offer a skip"): true = the song's audio before bar 1 does
   *  not play; absent = it plays as the lead-in (by marks, or with clips when the first clip starts on bar 1). Sent
   *  only to a bridge advertising `timeline_song_skip_intro`. */
  testTrack?: {
    id: string; name: string; pc?: string; output?: string; clips?: TimelineSongClip[]; skipIntro?: boolean;
    /** Mute / solo the song as a lane (2026-10-16, operator: "song row mute/solo"). Muted, or another lane soloed while
     *  it is not, the timeline plays WITHOUT its song (nothing is sent as `testTrack`). Soloed, every lane not soloed
     *  is silent, as a soloed lane silences the rest. Editor-side; OPBridge only sees the result. */
    mute?: boolean;
    solo?: boolean;
    /** The song's grid when `clips` were written (tempo pick, bar 1): a later change to either keeps each clip on
     *  the same MUSIC (the review, 2026-10-13: a tempo pick silently moved every clip). Editor-only. */
    clipsGrid?: { tempoChoice: 'as-read' | 'half' | 'double'; bar1Sec: number };
    /** The sections ARE the song's parts (2026-10-21, `pwa/src/timeline/songParts.ts`): one section per part in song
     *  order, the unnamed bars before the first part the lead-in, so a timeline bar is the song's bar. Kept in step
     *  with the song by the editor; the section structure is edited on the song, not here. Absent = the timeline's
     *  sections are its own (a song with no parts yet, or a timeline from before). Ignored while `clips` exist.
     *  Editor-only, never sent. */
    partsFollow?: boolean;
  };
  /** ISO 8601 timestamps for the library browser. `modified` is also the
   *  sync stamp: across devices the newer copy of a timeline wins. */
  created: string;
  modified: string;
  /** The app that MADE this timeline, set once when it is created and never changed by either app (operator,
   *  2026-09-23: "both should be able to edit but an app logo would tag the origin of the timeline"; agreed with
   *  OPBridge as an immutable `origin`, their DEC-579 amendment 2). Absent on timelines made before it existed, which
   *  were all made here (`timelineOrigin`). Either app edits any timeline in place; this only says where it came from. */
  origin?: TimelineOrigin;
}

// =====================================================================
// LIBRARY DOCUMENT
// =====================================================================

export const TIMELINES_SCHEMA_VERSION = 'opbuttons-timelines/v1' as const;

/** Single library holds all three tiers; UI filters by `tier` at pick sites
 *  (DEC-032). No per-tier sub-library. */
export interface TimelineLibrary {
  schemaVersion: typeof TIMELINES_SCHEMA_VERSION;
  timelines: Timeline[];
}

// =====================================================================
// HELPERS
// =====================================================================

/** Total bars in a timeline = its lead-in plus the sum of section lengths. Genre loops still
 *  report their unrolled length; runtime loop is a playback concern. */
export function timelineTotalBars(timeline: Timeline): number {
  let total = timelineLeadIn(timeline);
  for (const s of timeline.sections) total += Math.max(0, s.lengthBars | 0);
  return total;
}

/** The apps that make timelines (the `origin` both apps agreed, 2026-09-23). */
export type TimelineOrigin = 'opbuttons' | 'opbridge';

/** Where a timeline was made: its `origin`, or OPButtons for one made before the field existed (every such timeline was
 *  made here; OPBridge's own timelines carry their origin). */
export function timelineOrigin(timeline: Timeline): TimelineOrigin {
  return timeline.origin === 'opbridge' ? 'opbridge' : 'opbuttons';
}

/** Bars before the first section (`firstSectionBar`): 0 when absent. Whole bars, never negative. */
export function timelineLeadIn(timeline: Timeline): number {
  return Math.max(0, Math.floor(timeline.firstSectionBar ?? 0));
}

/** Whether the timeline starts with every light dark (`lightsAtStart`). Absent: a Song timeline does (a show starts
 *  from a known look), an Action timeline does not (it is fired from a button in the middle of something, and blacking
 *  the rig for a few bars of MIDI would be a surprise). */
export function timelineStartsDark(timeline: Timeline): boolean {
  return (timeline.lightsAtStart ?? (timeline.tier === 'song' ? 'off' : 'asIs')) === 'off';
}

/** Whether the lights the run drove end dark (`lightsAtEnd`). Absent: a Song timeline does, an Action timeline puts
 *  them back as they were (the same split as `timelineStartsDark`, for the same reason). */
export function timelineEndsDark(timeline: Timeline): boolean {
  return (timeline.lightsAtEnd ?? (timeline.tier === 'song' ? 'off' : 'restore')) === 'off';
}

/** The id the lead-in plays under (OPBridge names it in `songSectionId` while it plays). */
export const leadInSectionId = (timeline: Timeline): string => `${timeline.id}~leadin`;

/** The sections as they PLAY: with a lead-in, an unnamed section of that length first (auto-advance, never marked,
 *  so a song carries on through it). Everything that counts bars from the start (the playhead, the compile to
 *  OPBridge) reads this; everything that lists the operator's sections (pads, Pacer, Navigate) reads `sections`. */
export function timelinePlaySections(timeline: Timeline): TimelineSection[] {
  const n = timelineLeadIn(timeline);
  return n > 0 ? [{ id: leadInSectionId(timeline), name: '', lengthBars: n }, ...timeline.sections] : timeline.sections;
}

/** True when the timeline is over the tier's bar cap. Genre = uncapped;
 *  Song/Action = TIMELINE_MAX_BARS_SONG_ACTION. */
export function timelineOverCap(timeline: Timeline): boolean {
  if (timeline.tier === 'genre') return false;
  return timelineTotalBars(timeline) > TIMELINE_MAX_BARS_SONG_ACTION;
}

/** Effective whole-timeline loop state. `loop` undefined = off. Single source
 *  of truth so the editor preview, the launcher's `inherit` resolution, and any
 *  future runtime agree on what "does this timeline loop" means. */
export function timelineLoops(timeline: Timeline): boolean {
  return timeline.loop === true;
}

// =====================================================================
// UNKNOWN-FIELD PASSTHROUGH (the "envelope", 2026-09-17)
// =====================================================================
//
// WHAT THIS IS FOR. OPBridge asked (`OPApps/opbridge-opbuttons.md`,
// 2026-09-14) whether unknown fields could be PRESERVED rather than dropped
// on both sides, so a timeline can round-trip through either app without the
// other's additions being silently destroyed - and so the format can evolve
// on one side without a coordinated release. We answered yes and committed
// to our half. This is it.
//
// WHAT WE MEASURED FIRST, because the commitment assumed more than was true.
// `scratchpad/probe-timeline-passthrough.mts` plants an unknown field at all
// five levels (library / timeline / section / lane / step) and walks them
// through the real store. Result: our LIBRARY DOCUMENT is already lossless -
// `load()` is a `JSON.parse` + spread, and all 28 store mutations, undo/redo
// and `duplicateTimeline` spread rather than rebuild, so nothing is dropped
// at any level. TypeScript interfaces do not strip at runtime the way
// OPBridge's Rust structs do. The ONLY place we lose fields is
// `bridgeSync.ts::toBridgeTimeline`, which rebuilds every level as an object
// literal. So the envelope is one converter's problem, not five levels of
// store plumbing - and `smokes/timeline_unknown_fields_survive.ts` pins BOTH
// halves, because the document half is currently lossless by accident (every
// mutation happens to spread) rather than by construction.
//
// WHY THE KEY MAPS ARE `Record<keyof T, true>` AND NOT A STRING ARRAY. Adding
// a field to one of these interfaces without adding it here is a COMPILE
// ERROR, which is the only thing that keeps the maps honest. A `string[]`
// would rot silently and start leaking newly-added schema fields onto the
// wire as if they were foreign. Same "make it structural, not conventional"
// argument OPStage made about the refusal envelope (CP-136) - a rule a future
// session can forget is not a rule.
//
// WHY "UNKNOWN" MEANS "ABSENT FROM *OUR* SCHEMA", NOT "ABSENT FROM THE WIRE".
// `laneKind` and `fixtureRef` are known to us and deliberately NOT emitted
// yet - PLAN.md sequences the DMX lane binding AFTER our own DMX authoring
// settles, because emitting a shape we are still revising hands OPBridge a
// moving target. Keying off our own schema is what keeps the envelope-now /
// content-later split intact: this ships the envelope without smuggling the
// content across with it.

/** Every key `TimelineLibrary` itself defines. See the block comment above
 *  for why this is a keyed map rather than an array. */
export const TIMELINE_LIBRARY_KNOWN_KEYS: Record<keyof TimelineLibrary, true> = {
  schemaVersion: true, timelines: true,
};

/** Every key `Timeline` itself defines. */
export type TimelineTouchMode = 'nextBarAfterRelease' | 'pickup' | 'untilStop';
export type TimelineLightsAtStart = 'off' | 'asIs';
export type TimelineLightsAtEnd = 'off' | 'restore';

export const TIMELINE_KNOWN_KEYS: Record<keyof Timeline, true> = {
  id: true, name: true, tier: true, form: true, purpose: true, sections: true, lanes: true,
  defaultSnap: true, signature: true, bpm: true, loop: true, touchMode: true, arrival: true, firstSectionBar: true,
  lightsAtStart: true, lightsAtEnd: true, instrumentLooks: true, strobes: true, projectId: true, testTrack: true,
  created: true, modified: true, origin: true,
};

/** Every key `TimelineSection` itself defines. */
export const TIMELINE_SECTION_KNOWN_KEYS: Record<keyof TimelineSection, true> = {
  id: true, name: true, lengthBars: true, color: true, onEnd: true, onEnter: true, arrival: true,
};

/** Every key `TimelineLane` itself defines. `laneKind` / `fixtureRef` are in
 *  here deliberately - they are ours and their absence from the wire is a
 *  sequencing decision, not a gap this helper should quietly close. */
export const TIMELINE_LANE_KNOWN_KEYS: Record<keyof TimelineLane, true> = {
  id: true, name: true, laneKind: true, color: true, steps: true,
  fixtureRef: true, lightGroup: true, allLights: true, mute: true, solo: true, autoFire: true, control: true, vizKnob: true, opvControl: true, motion: true,
};

/** Every key `CueStep` itself defines (including inherited `StepBase`).
 *  Only the `cue` kind gets a map: it is the one step kind that crosses the
 *  wire 1:1. Every other kind is either dropped by the down-converter or
 *  COMPILED into 0..N cues with synthesised ids, and a source step's
 *  unknown field has no single derived cue to belong to - see
 *  `bridgeSync.ts::resolveStepToCues`. */
export const TIMELINE_CUE_STEP_KNOWN_KEYS: Record<keyof CueStep, true> = {
  id: true, position: true, snap: true, kind: true, action: true, label: true,
};

/** The keys of `obj` that our own schema does not name, as a plain object -
 *  or `null` when there are none.
 *
 *  `null` rather than `{}` on the empty case is load-bearing: it lets a
 *  caller spread nothing at all, so a document with no foreign fields
 *  produces a BYTE-IDENTICAL wire payload to before this existed. That keeps
 *  the change a no-op for the live rig - no spurious re-push, no cache churn
 *  in `autoSync.ts::isTimelinePushCurrent`, which compares wire JSON. */
export function unknownFieldsOf<T extends object>(
  obj: T,
  knownKeys: Record<keyof T, true>,
): Record<string, unknown> | null {
  let extras: Record<string, unknown> | null = null;
  for (const key of Object.keys(obj)) {
    if (Object.hasOwn(knownKeys, key)) continue;
    extras ??= {};
    extras[key] = (obj as Record<string, unknown>)[key];
  }
  return extras;
}

/** Type guards for step kind narrowing. */
export const isCueStep = (s: TimelineStep): s is CueStep => s.kind === 'cue';
export const isAnimationStep = (s: TimelineStep): s is AnimationStep => s.kind === 'animation';
export const isSequencerRefStep = (s: TimelineStep): s is SequencerRefStep => s.kind === 'sequencer';
export const isMacroRefStep = (s: TimelineStep): s is MacroRefStep => s.kind === 'macro';
export const isButtonRefStep = (s: TimelineStep): s is ButtonRefStep => s.kind === 'buttonRef';
export const isColorMarkerStep = (s: TimelineStep): s is ColorMarkerStep => s.kind === 'colorMarker';
export const isLightReleaseStep = (s: TimelineStep): s is LightReleaseStep => s.kind === 'lightRelease';
export const isMarkerStep = (s: TimelineStep): s is MarkerStep => s.kind === 'marker';
export const isTargetValueStep = (s: TimelineStep): s is TargetValueStep => s.kind === 'targetValue';
export const isPatternStep = (s: TimelineStep): s is PatternStep => s.kind === 'pattern';
export const isHitRouteStep = (s: TimelineStep): s is HitRouteStep => s.kind === 'hitRoute';
export const isHitBlockStep = (s: TimelineStep): s is HitBlockStep => s.kind === 'hitBlock';
export const isHitStep = (s: TimelineStep): s is HitStep => s.kind === 'hit';

/** The id of a key a pattern was written out to: `<pattern id>~<n>`. `patternOf` reads the pattern's id back, so a
 *  warning OPBridge gives on a written-out key lands on its block. */
export const PATTERN_KEY_SEP = '~';
export function patternOf(stepId: string): string | null {
  const i = stepId.indexOf(PATTERN_KEY_SEP);
  return i > 0 ? stepId.slice(0, i) : null;
}

/** A pattern's keys written out as colour keyframes, in time order, from `position` to `position + durationBars`. */
export function unrollPattern(p: PatternStep): ColorMarkerStep[] {
  const out: ColorMarkerStep[] = [];
  const cyc = p.cycleBars;
  if (!(cyc > 0) || !(p.durationBars > 0) || p.keys.length === 0) return out;
  const keys = [...p.keys].filter((k) => k.at >= 0 && k.at < cyc).sort((a, b) => a.at - b.at);
  const end = p.position + p.durationBars;
  let n = 0;
  for (let c = 0; p.position + c * cyc < end - 1e-9; c++) {
    for (const k of keys) {
      const pos = Math.round((p.position + c * cyc + k.at) * 1e6) / 1e6;
      if (pos >= end - 1e-9) break;
      out.push({
        id: `${p.id}${PATTERN_KEY_SEP}${n++}`, kind: 'colorMarker', position: pos, color: { ...k.color },
        ...(k.label !== undefined ? { label: k.label } : {}), ...(k.curve !== undefined ? { curve: k.curve } : {}),
        ...(k.space !== undefined ? { space: k.space } : {}), ...(k.path !== undefined ? { path: k.path } : {}),
      });
    }
  }
  return out;
}

const unrolled = new WeakMap<readonly TimelineStep[], TimelineStep[]>();
/** The id of a split keyframe's ARRIVING key as it plays (`ColorMarkerStep.arrive`). */
export const arrivalKeyId = (stepId: string): string => `${stepId}${PATTERN_KEY_SEP}in`;
/** A lane's steps as they PLAY: every pattern written out as keyframes, and the plain keyframes and Hand backs inside
 *  a pattern's span dropped (the block owns it); a split keyframe (`arrive`) as two keys on its beat, the arriving one
 *  first (the sort is stable). The same array back when the lane holds neither, and the same answer for the same
 *  array (cached), so every colour reader can call it on every frame. */
export function unrollPatterns(steps: readonly TimelineStep[]): TimelineStep[] {
  if (!steps.some((s) => isPatternStep(s) || (s.kind === 'colorMarker' && !!s.arrive))) return steps as TimelineStep[];
  const hit = unrolled.get(steps);
  if (hit) return hit;
  const pats = steps.filter(isPatternStep);
  const inside = (pos: number) => pats.some((p) => pos >= p.position - 1e-9 && pos < p.position + p.durationBars - 1e-9);
  const out: TimelineStep[] = [];
  for (const s of steps) {
    if (isPatternStep(s)) out.push(...unrollPattern(s));
    else if ((s.kind === 'colorMarker' || s.kind === 'lightRelease') && inside(s.position)) continue;
    else if (s.kind === 'colorMarker' && s.arrive) {
      // The FADE-IN goes with the arriving key (audit 2026-11-03 C-7): it is
      // the colour the lights fade into, and a master's takeover takes its
      // fade from its first key (`lightMix.ts takeoversOf`, and OPBridge's
      // port, which gets these keys written out). On the leaving key the fade
      // was read from the arriving key, which had none: a split keyframe that
      // started an All-lights takeover lost its fade-in.
      const { arrive, leadInBars, leadInCurve, ...leaving } = s;
      out.push({
        id: arrivalKeyId(s.id), kind: 'colorMarker', position: s.position, color: { ...arrive }, curve: 'hold',
        ...(leadInBars !== undefined ? { leadInBars } : {}), ...(leadInCurve !== undefined ? { leadInCurve } : {}),
      }, leaving);
    } else out.push(s);
  }
  out.sort((a, b) => a.position - b.position);
  unrolled.set(steps, out);
  return out;
}

/** The look id a route names to light NOTHING: OPBridge finds no look of that id, so the hit is routed nowhere and
 *  spends no flash (`live_lights::route_on` / `render_routed`, read 2026-10-23). How a drum lights block ends, so its
 *  last look does not keep playing past it. Starts with `~`, which no id we make does. */
export const HIT_SILENCE_LOOK = '~off';

/** The id a block's look is sent under. */
export const hitBlockLookId = (blockId: string, which: 'main' | 'fill'): string => `${blockId}${PATTERN_KEY_SEP}${which}`;

/** A block's fill when it plays (on, and at least a beat long), else null. */
export function hitBlockFill(b: HitBlockStep): { bars: number; look: HitBlockLook } | null {
  const f = b.fill;
  return f && !f.off && f.bars >= 0.25 - 1e-9 ? f : null;
}

/** Where each cycle of a block starts, and where its fill starts (null = no fill in that cycle: none, or the cycle is
 *  cut short before it). A fill as long as the cycle IS the cycle. */
export function hitBlockCycles(b: HitBlockStep): Array<{ start: number; end: number; fillAt: number | null }> {
  const out: Array<{ start: number; end: number; fillAt: number | null }> = [];
  const cyc = b.cycleBars;
  if (!(cyc > 0) || !(b.durationBars > 0)) return out;
  const end = b.position + b.durationBars;
  const fill = hitBlockFill(b);
  for (let c = 0; b.position + c * cyc < end - 1e-9 && c < 4096; c++) {
    const start = b.position + c * cyc;
    const cycEnd = Math.min(end, start + cyc);
    const fs = fill ? start + Math.max(0, cyc - fill.bars) : null;
    out.push({ start, end: cycEnd, fillAt: fs !== null && fs < cycEnd - 1e-9 ? fs : null });
  }
  return out;
}

/** A block written out as routes: per cycle one to its look where the cycle starts (unless the fill fills it) and one
 *  to its fill where that starts; then, when it ends before `untilBar`, one to nothing (`HIT_SILENCE_LOOK`). Ids
 *  `<block id>~<n>`, so a warning on a route lands on its block (`patternOf`). */
export function unrollHitBlock(b: HitBlockStep, untilBar = Number.POSITIVE_INFINITY): HitRouteStep[] {
  const out: HitRouteStep[] = [];
  let n = 0;
  const route = (pos: number, lookId: string) => out.push({
    id: `${b.id}${PATTERN_KEY_SEP}${n++}`, kind: 'hitRoute', position: Math.round(pos * 1e6) / 1e6,
    source: { ...b.source }, lookId,
  });
  const cycles = hitBlockCycles(b);
  for (const c of cycles) {
    if (c.fillAt === null || c.fillAt > c.start + 1e-9) route(c.start, hitBlockLookId(b.id, 'main'));
    if (c.fillAt !== null) route(c.fillAt, hitBlockLookId(b.id, 'fill'));
  }
  const end = b.position + b.durationBars;
  if (cycles.length && end < untilBar - 1e-9) route(end, HIT_SILENCE_LOOK);
  return out;
}

/** The key two sources share when they are the same source (a route holds until the next one for the SAME source). */
export function hitSourceKey(s: HitSource): string {
  return JSON.stringify([s.role?.toLowerCase() ?? null, s.track?.toLowerCase() ?? null, s.note ?? null, s.port?.toLowerCase() ?? null]);
}

/** An instrument lane's routes as they PLAY, in time order: every block written out, and a plain route inside a block's
 *  span for the block's source dropped (the block owns its span, as a pattern does). On one bar a route to nothing
 *  comes first, so a block starting where another ends wins that bar (OPBridge takes the LAST route at or before). */
export function unrollHitBlocks(steps: readonly TimelineStep[], untilBar = Number.POSITIVE_INFINITY): HitRouteStep[] {
  const blocks = steps.filter(isHitBlockStep);
  const owns = (r: HitRouteStep) => blocks.some((b) => hitSourceKey(b.source) === hitSourceKey(r.source)
    && r.position >= b.position - 1e-9 && r.position < b.position + b.durationBars - 1e-9);
  const out: HitRouteStep[] = [];
  for (const s of steps) {
    if (isHitBlockStep(s)) out.push(...unrollHitBlock(s, untilBar));
    else if (isHitRouteStep(s) && !owns(s)) out.push(s);
  }
  return out.sort((a, b) => a.position - b.position
    || (a.lookId === HIT_SILENCE_LOOK ? 0 : 1) - (b.lookId === HIT_SILENCE_LOOK ? 0 : 1));
}

/** A block's look as OPBridge reads it, its `lights` resolved by the caller (every light, when `allLights`). */
export function hitBlockWireLook(id: string, name: string, l: HitBlockLook, lights: readonly string[]): InstrumentLook {
  const colors = l.colors.length ? l.colors : [{ r: 1, g: 1, b: 1 }];
  const plain = (c: InstrumentColor): InstrumentColor => ({ r: c.r, g: c.g, b: c.b, ...(c.w !== undefined ? { w: c.w } : {}) });
  return {
    id, name, lights: [...lights], style: l.style, vary: l.vary, fallBeats: l.fallBeats, color: plain(colors[0]!),
    ...(colors.length > 1 ? { palette: colors.map(plain) } : {}),
    ...(l.attackBeats ? { attackBeats: l.attackBeats } : {}),
    ...(l.curve && l.curve !== 'easeOut' ? { curve: l.curve } : {}),
    ...(l.level !== undefined && l.level < 1 ? { level: Math.max(0, l.level) } : {}),
    // Absent = 1 on both sides, so only a softer setting is written.
    ...(l.velocitySensitivity !== undefined && Number.isFinite(l.velocitySensitivity) && l.velocitySensitivity < 1
      ? { velocitySensitivity: Math.max(0, l.velocitySensitivity) } : {}),
  };
}
